import React from "react";

import { Box, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { makeStyles } from "tss-react/mui";

import CircularProgressBar from "@/components/AllergenModal/CircularProgressBar";
import CalorieFireIcon from "@/components/Icons/CalorieFireIcon";
import { colors } from "@/theme/overrides/colors";
import { percent, round } from "@/utils/number";

import { useRecipe } from "../store/RecipeStore";

const useStyles = makeStyles()(() => ({
  progressContainer: {
    alignItems: "center",
    backgroundColor: colors.white,
    borderRadius: 100,
    display: "flex",
    gap: 20,
    padding: "12px 24px 12px 12px",
  },
  caloriesContainer: {
    backgroundColor: colors.white,
    padding: "32px",
  },
  bodyText4: {
    fontWeight: 600,
    fontSize: 16,
    color: "#595F68",
  },
}));

const Macronutrients: React.FC = () => {
  const { classes, cx } = useStyles();
  const recipe = useRecipe();

  const {
    calories,
    carbohydrates,
    proteins,
    fats,
    totalCarbsGrams,
    totalProteinGrams,
    totalFatGrams,
  } = recipe.totalMacros;

  return (
    <Box>
      <Typography fontSize={20} fontWeight={700} variant="body1">
        Macronutrients
      </Typography>
      <Box height={6} />
      <Box
        alignItems="center"
        bgcolor={colors.light}
        display="flex"
        gap={5}
        padding={2}
      >
        <Box
          className={cx(classes.progressContainer, classes.caloriesContainer)}
        >
          <Box fontSize="48px">
            <CalorieFireIcon fontSize="inherit" />
          </Box>
          <Box>
            <Typography
              textAlign="center"
              fontWeight={700}
              fontSize={28}
              color={colors.dark}
            >
              {round(calories, 4)}
            </Typography>
            <Typography
              textAlign="center"
              fontWeight={600}
              fontSize={14}
              color="#595F68"
            >
              Calories
            </Typography>
          </Box>
        </Box>
        <Box className={classes.progressContainer}>
          <CircularProgressBar
            size="small"
            calories={`${percent(carbohydrates, { round: 0 })}%`}
            label={`${totalCarbsGrams}g`}
            data={[
              {
                value: percent(carbohydrates),
                color: colors.primary,
              },
            ]}
          />
          <Typography className={classes.bodyText4}>Carbs</Typography>
        </Box>
        <Box className={classes.progressContainer}>
          <CircularProgressBar
            size="small"
            calories={`${percent(proteins, {
              round: 0,
            })}%`}
            label={`${totalProteinGrams}g`}
            data={[
              {
                value: percent(proteins),
                color: colors.secondary,
              },
            ]}
          />
          <Typography className={classes.bodyText4}>Protein</Typography>
        </Box>
        <Box className={classes.progressContainer}>
          <CircularProgressBar
            size="small"
            calories={`${percent(fats, { round: 0 })}%`}
            label={`${totalFatGrams}g`}
            data={[
              {
                value: percent(fats),
                color: "#2D87BB",
              },
            ]}
          />
          <Typography className={classes.bodyText4}>Fats</Typography>
        </Box>
      </Box>
      <Box margin={0.5} />
      <Typography fontSize={14}>
        Note: The calories and macronutrients are automatically generated based
        on the ingredients added.
      </Typography>
    </Box>
  );
};

export default observer(Macronutrients);
